/* Bag drawer: opens from the header and after any add-to-bag; quantity, remove, gift wrap,
   gift note and hide-prices all go through the Cart AJAX API with bundled section
   rendering, then the drawer's content and the header count are swapped in place.
   The same element runs the /cart page (data-cart-page): there it never opens a drawer and
   re-renders its own section instead of the drawer's. Requests run one at a time, lines are
   addressed by their line key, and focus plus a status message survive every re-render. */

(() => {
  'use strict';

  class AntartiCartDrawer extends HTMLElement {
    connectedCallback() {
      this.cfg = JSON.parse(this.querySelector('[data-cart-config]').textContent);
      this.dialog = this.querySelector('#CartDrawer');
      this.isPage = this.hasAttribute('data-cart-page');
      /* The bag page renders its own section; everywhere else the drawer renders cart-drawer. */
      this.sectionKey = this.isPage ? this.dataset.section : 'cart-drawer';
      this.sections = `${this.sectionKey},cart-icon-bubble`;
      this.cleanups = [];
      this.noteTimer = null;
      this.pendingNote = null;
      this.returnFocus = null;
      this.queue = Promise.resolve();
      this.adding = new WeakSet();
      this.labelTimers = new WeakMap();

      /* Open from any [data-cart-open] (the header bag icon). */
      this.onDocClick = (e) => {
        const opener = e.target.closest('[data-cart-open]');
        if (opener) {
          /* On /cart there is no drawer: the bag link loads the page like a normal link. */
          if (this.isPage) return;
          e.preventDefault();
          this.open();
          return;
        }
        if (e.target.closest('[data-cart-close]')) {
          if (!this.isPage) this.dialog.close();
          return;
        }
        const qty = e.target.closest('[data-cart-qty]');
        if (qty && this.contains(qty)) {
          const key = qty.dataset.cartQty;
          const delta = Number(qty.dataset.delta);
          /* Read the quantity when the queued request runs, so quick repeat presses add up. */
          this.change(key, () => {
            const input = this.querySelector(`[data-cart-qty-input="${CSS.escape(key)}"]`);
            return input ? Math.max(0, Number(input.value || 0) + delta) : null;
          });
          return;
        }
        const remove = e.target.closest('[data-cart-remove]');
        if (remove && this.contains(remove)) this.change(remove.dataset.cartRemove, 0);
      };
      document.addEventListener('click', this.onDocClick);
      this.cleanups.push(() => document.removeEventListener('click', this.onDocClick));

      /* Add-to-bag forms anywhere on the page. */
      this.onSubmit = (e) => {
        const form = e.target.closest('[data-product-form], [data-quick-add]');
        if (!form) return;
        e.preventDefault();
        this.add(form, e.submitter);
      };
      document.addEventListener('submit', this.onSubmit);
      this.cleanups.push(() => document.removeEventListener('submit', this.onSubmit));

      /* A page restored from the back/forward cache may show a bag that has changed since. */
      this.onRestore = () => this.refresh();
      document.addEventListener('antarti:restore', this.onRestore);
      this.cleanups.push(() => document.removeEventListener('antarti:restore', this.onRestore));

      /* Quantity typed directly, gift wrap, hide prices, gift note. A failed request puts the
         control back to what the bag actually holds. */
      this.onChange = (e) => {
        const t = e.target;
        if (t.matches('[data-cart-qty-input]')) {
          this.change(t.dataset.cartQtyInput, Math.max(0, Number(t.value || 0))).then((data) => {
            if (!data) t.value = t.defaultValue;
          });
        } else if (t.matches('[data-gift-wrap]')) this.toggleGiftWrap(t);
        else if (t.matches('[data-cart-attribute-flag]')) {
          this.updateAttributes({ [t.dataset.cartAttributeFlag]: t.checked ? 'Yes' : '' }).then((data) => {
            if (!data) t.checked = !t.checked;
          });
        } else if (t.matches('[data-cart-attribute]')) this.flushNote();
      };
      this.onInput = (e) => {
        const t = e.target;
        if (!t.matches('[data-cart-attribute]')) return;
        this.pendingNote = { key: t.dataset.cartAttribute, value: t.value };
        clearTimeout(this.noteTimer);
        this.noteTimer = setTimeout(() => this.flushNote(), 600);
      };
      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);

      /* Close on backdrop click. */
      this.onDialogClick = (e) => {
        if (e.target !== this.dialog) return;
        const r = this.dialog.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) this.dialog.close();
      };
      this.dialog.addEventListener('click', this.onDialogClick);

      /* On close: drop any old error, and send focus back to the Add button that opened the
         drawer if the browser left it on the page body. */
      this.onDialogClose = () => {
        this.hideError();
        const back = this.returnFocus;
        this.returnFocus = null;
        const active = document.activeElement;
        if (back?.isConnected && (!active || active === document.body || this.dialog.contains(active))) back.focus();
      };
      this.dialog.addEventListener('close', this.onDialogClose);
    }

    disconnectedCallback() {
      this.cleanups.forEach((fn) => fn());
      clearTimeout(this.noteTimer);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('input', this.onInput);
      this.dialog?.removeEventListener('click', this.onDialogClick);
      this.dialog?.removeEventListener('close', this.onDialogClose);
    }

    open() {
      /* The bag page has only a hidden stub dialog; showing it would make the page inert. */
      if (this.isPage) return;
      if (!this.dialog.open) this.dialog.showModal();
      this.querySelector('[data-cart-close]')?.focus();
    }

    setBusy(on) {
      this.classList.toggle('is-busy', on);
      this.setAttribute('aria-busy', on ? 'true' : 'false');
    }

    showError(message) {
      const el = this.querySelector('[data-cart-error]');
      if (!el) return;
      el.textContent = message || this.cfg.errorGeneric;
      el.hidden = false;
    }

    hideError() {
      const el = this.querySelector('[data-cart-error]');
      if (!el) return;
      el.hidden = true;
      el.textContent = '';
    }

    /* Requests run one after another. A job is { url, body }, or a function returning one (or
       null to skip) that is called when its turn comes, so it sees the latest bag. A failed
       job carries its message in job.error. */
    enqueue(task) {
      const run = this.queue.then(task);
      this.queue = run.catch(() => {});
      return run;
    }

    request(job, opts) {
      return this.enqueue(() => this.send(typeof job === 'function' ? job() : job, opts));
    }

    async send(job, { render = true, busy = true, announce = false, quiet = false } = {}) {
      if (!job) return null;
      job.error = '';
      if (busy) this.setBusy(true);
      try {
        const res = await fetch(job.url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ ...job.body, sections: this.sections, sections_url: window.location.pathname }),
        });
        const data = await res.json();
        if (!res.ok || data.status) {
          job.error = data.description || data.message || '';
          if (!quiet) this.showError(job.error);
          return null;
        }
        if (render) this.render(data.sections, announce);
        else this.hideError();
        document.dispatchEvent(new CustomEvent('antarti:cart', { detail: data }));
        return data;
      } catch {
        if (!quiet) this.showError();
        return null;
      } finally {
        if (busy) this.setBusy(false);
      }
    }

    /* Re-fetch both sections with a GET (after a back/forward cache restore). */
    refresh() {
      return this.enqueue(async () => {
        try {
          const res = await fetch(`${window.location.pathname}?sections=${this.sections}`);
          if (res.ok) this.render(await res.json());
        } catch {
          /* Keep what is on screen; the next change re-renders it. */
        }
      });
    }

    render(sections, announce) {
      if (!sections) return;
      const html = sections[this.sectionKey];
      if (html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-cart-content]');
        const mine = this.querySelector('[data-cart-content]');
        if (fresh && mine) {
          const focus = this.focusMarker(mine);
          mine.innerHTML = fresh.innerHTML;
          /* A gift note typed but not saved yet keeps its text. */
          const note = this.pendingNote;
          if (note) {
            const field = mine.querySelector(`[data-cart-attribute="${CSS.escape(note.key)}"]`);
            if (field) field.value = note.value;
          }
          if (focus) this.restoreFocus(mine, focus);
          if (announce) this.announce(mine);
        }
        const count = doc.querySelector('[data-cart-count-label]');
        const mineCount = this.querySelector('[data-cart-count-label]');
        if (count && mineCount) mineCount.textContent = count.textContent;
      }
      const bubbleHtml = sections['cart-icon-bubble'];
      if (bubbleHtml) {
        const doc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
        const fresh = doc.querySelector('#cart-icon-bubble');
        document.querySelectorAll('#cart-icon-bubble').forEach((el) => fresh && el.replaceWith(fresh.cloneNode(true)));
      }
    }

    /* Remembers which control had focus inside the content that is about to be replaced. */
    focusMarker(root) {
      const el = document.activeElement;
      if (!el || el === root || !root.contains(el)) return null;
      const line = el.closest('[data-cart-line]');
      const pos = line ? [...root.querySelectorAll('[data-cart-line]')].indexOf(line) : -1;
      let selector = el.id ? `#${CSS.escape(el.id)}` : null;
      if (!selector && el.hasAttribute('data-cart-qty')) {
        selector = `[data-cart-qty="${CSS.escape(el.dataset.cartQty)}"][data-delta="${CSS.escape(el.dataset.delta)}"]`;
      }
      if (!selector) {
        const attr = ['data-cart-qty-input', 'data-cart-remove', 'data-gift-wrap', 'data-cart-attribute-flag', 'data-cart-attribute'].find((a) => el.hasAttribute(a));
        if (attr) selector = `[${attr}="${CSS.escape(el.getAttribute(attr))}"]`;
      }
      return { selector, pos };
    }

    /* Focuses the same control again. If its line is gone: the line now in that place (or the
       last one), and when the bag is empty, the heading. */
    restoreFocus(root, { selector, pos }) {
      let target = selector ? root.querySelector(selector) : null;
      if (!target && pos >= 0) {
        const lines = root.querySelectorAll('[data-cart-line]');
        const line = lines[Math.min(pos, lines.length - 1)];
        target = line?.querySelector('.cart-line__title a, button, input');
      }
      target = target || this.querySelector('[data-cart-heading]');
      target?.focus();
    }

    /* Writes the new subtotal and free-shipping text into the persistent status region, which
       sits outside the replaced content so screen readers announce it. */
    announce(root) {
      const status = this.querySelector('[data-cart-status]');
      if (!status) return;
      const subtotal = root.querySelector('[data-cart-subtotal]');
      const parts = [subtotal ? `${this.cfg.subtotal} ${subtotal.textContent.trim()}` : this.cfg.empty];
      const ship = root.querySelector('[data-free-shipping-bar] .ship-bar__text');
      if (ship && ship.textContent.trim()) parts.push(ship.textContent.trim());
      status.textContent = '';
      requestAnimationFrame(() => (status.textContent = parts.join('. ')));
    }

    async add(form, submitter) {
      const idInput = form.querySelector('[name="id"]');
      if (!idInput || this.adding.has(form)) return;
      /* Every button that submits this form, including the product page's sticky-bar button,
         which sits outside the form with form="…". aria-disabled (not disabled) keeps keyboard
         focus on the pressed button; the in-flight guard above stops repeat submits. */
      const buttons = [...form.elements].filter((el) => el.matches('button[type="submit"], [data-add-to-bag]'));
      const before = buttons.map((b) => b.getAttribute('aria-disabled'));
      const errorSlot = form.querySelector('[data-form-error]');
      this.adding.add(form);
      buttons.forEach((b) => b.setAttribute('aria-disabled', 'true'));
      if (errorSlot) {
        errorSlot.hidden = true;
        errorSlot.textContent = '';
      }
      /* Line item properties go with it: the size picked on the product page (S35) is one, and its
         inputs sit outside the form with form="…", which FormData includes. */
      const item = { id: Number(idInput.value), quantity: 1 };
      const properties = {};
      for (const [key, value] of new FormData(form)) {
        const name = /^properties\[(.+)\]$/.exec(key)?.[1];
        if (name && value !== '') properties[name] = value;
      }
      if (Object.keys(properties).length) item.properties = properties;
      const job = { url: this.cfg.addUrl, body: { items: [item] } };
      const data = await this.request(job, { quiet: true });
      this.adding.delete(form);
      buttons.forEach((b, i) => (before[i] === null ? b.removeAttribute('aria-disabled') : b.setAttribute('aria-disabled', before[i])));

      if (!data) {
        /* Sold out, or all the stock is already in the bag: say so where the shopper is. The
           product page has its own alert under the button; a card's quick add does not, and
           the product page's sticky bar shows only once that alert has scrolled out of view,
           so for those the drawer opens with the message instead. */
        const message = job.error || this.cfg.errorGeneric;
        if (errorSlot && (!submitter || form.contains(submitter))) {
          errorSlot.textContent = message;
          errorSlot.hidden = false;
        } else {
          this.showError(message);
          this.returnFocus = submitter || null;
          this.open();
          /* The alert is the last thing in the scrolling body; with lines in the bag it can open
             below the fold on a phone. Focus stays on the close button in the drawer head. */
          this.querySelector('[data-cart-error]')?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
        }
        return;
      }

      /* One pending restore per label. A repeat add inside the window keeps the original label
         (not "Added"), and the restore only runs if the label still says "Added", so a Sold out
         or Price coming soon label written by a variant change is never overwritten. */
      buttons.forEach((b) => {
        const span = b.querySelector('[data-add-label]') || b;
        const pending = this.labelTimers.get(span);
        if (pending) clearTimeout(pending.timer);
        this.labelTimers.delete(span);
        const label = pending && span.textContent === this.cfg.added ? pending.label : span.textContent;
        if (!label) return;
        span.textContent = this.cfg.added;
        const timer = setTimeout(() => {
          this.labelTimers.delete(span);
          if (span.textContent === this.cfg.added) span.textContent = label;
        }, 1600);
        this.labelTimers.set(span, { timer, label });
      });
      this.returnFocus = submitter || (form.contains(document.activeElement) ? document.activeElement : null);
      this.open();
    }

    change(key, quantity) {
      return this.request(() => {
        const q = typeof quantity === 'function' ? quantity() : quantity;
        return q === null ? null : this.lineJob(key, q);
      }, { announce: true });
    }

    /* A quantity change by line key (stable across re-renders and other tabs). Removing the
       last piece also removes a gift-wrap line that would otherwise be left on its own. */
    lineJob(key, quantity) {
      if (quantity === 0) {
        const lines = [...this.querySelectorAll('[data-cart-line]')];
        const target = lines.find((l) => l.dataset.lineKey === key);
        /* Already gone (a repeat Remove press queued behind the first): nothing to send. */
        if (!target) return null;
        const rest = lines.filter((l) => l !== target);
        if (target && !target.hasAttribute('data-gift-wrap-line') && rest.length && rest.every((l) => l.hasAttribute('data-gift-wrap-line'))) {
          const updates = { [key]: 0 };
          rest.forEach((l) => (updates[l.dataset.lineKey] = 0));
          return { url: this.cfg.updateUrl, body: { updates } };
        }
      }
      return { url: this.cfg.changeUrl, body: { id: key, quantity } };
    }

    /* Attributes change nothing else on screen, so the content is not re-rendered (that would
       replace the gift note while someone is typing in it). */
    updateAttributes(attributes) {
      return this.request({ url: this.cfg.updateUrl, body: { attributes } }, { render: false, busy: false });
    }

    flushNote() {
      clearTimeout(this.noteTimer);
      this.noteTimer = null;
      /* The note stays pending until its save lands, so a render queued ahead of the save
         keeps the typed text instead of the old saved note. A note already being saved is
         not sent twice (the timer and leaving the field both flush). */
      const note = this.pendingNote;
      if (!note) return Promise.resolve(null);
      if (!note.saving) {
        note.saving = this.updateAttributes({ [note.key]: note.value.trim() }).then((data) => {
          if (this.pendingNote === note) {
            if (data) this.pendingNote = null;
            else note.saving = null; /* failed: leaving the field tries again */
          }
          return data;
        });
      }
      return note.saving;
    }

    async toggleGiftWrap(checkbox) {
      const variantId = Number(checkbox.dataset.giftWrap);
      let data;
      if (checkbox.checked) {
        data = await this.request({ url: this.cfg.addUrl, body: { items: [{ id: variantId, quantity: 1 }] } }, { announce: true });
      } else {
        const line = this.querySelector(`[data-cart-line][data-variant-id="${variantId}"]`);
        if (!line) return;
        data = await this.change(line.dataset.lineKey, 0);
      }
      if (!data) checkbox.checked = !checkbox.checked;
    }
  }

  (window.antarti?.define || ((n, c) => customElements.get(n) || customElements.define(n, c)))('antarti-cart-drawer', AntartiCartDrawer);
})();
